import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { cartContext } from 'contexts/cartContext';
import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import { getUri } from 'services/generic';
import {
  getCatalogSku,
  getCup,
  getFinalPrice,
  getRealSku,
  getSizeLabel,
} from 'services/product';
import { EDIT_URL_REGEX } from 'settings/regex';
import {
  ProductType,
  SelectedProductAttributes,
} from 'types/ProductPageV2/Products';
import { Cart } from 'types/Cart';

type UseAddToBasketType = {
  editLine: string | null;
  addToBasket: (
    product: ProductType,
    qty: number,
    gtmMandatoryAttributes: SelectedProductAttributes[],
    openModal: (idModal: string) => void,
    closeModal: () => void
  ) => Promise<Cart | undefined>;
  updateBasket: (
    product: ProductType,
    qty: number,
    line: string
  ) => Promise<Cart | undefined>;
};

export function useAddToBasket(): UseAddToBasketType {
  const { pathname } = useLocation();
  const { LSModeEnabled } = useLargeSizeMode();
  const { addToMyBasket, updateMyBasket } = useContext(cartContext);

  async function addToBasket(
    product: ProductType,
    qty: number,
    gtmMandatoryAttributes: SelectedProductAttributes[],
    openModal: (idModal: string) => void,
    closeModal: () => void
  ) {
    const { Id, Url, Attributes } = product;

    return addToMyBasket(
      {
        catalogSku: getCatalogSku(Attributes),
        sku: getRealSku(Attributes),
        qty: qty,
        url: getUri(Url),
        sizeLabel: getSizeLabel(Attributes),
        cupLabel: getCup(Attributes),
        productData: {
          ...Attributes,
          ...gtmMandatoryAttributes,
        },
        isLSProduct: LSModeEnabled,
      },
      'fiche produit',
      {
        BasketLines: [
          {
            ProductId: Id,
            Quantity: qty,
            Price: getFinalPrice(Attributes),
          },
        ],
      },
      Attributes,
      openModal,
      closeModal
    );
  }

  async function updateBasket(product: ProductType, qty: number, line: string) {
    const { Id, Url, Attributes } = product;

    return updateMyBasket(
      {
        catalogSku: getCatalogSku(Attributes),
        sku: getRealSku(Attributes),
        qty: qty,
        url: getUri(Url),
        sizeLabel: getSizeLabel(Attributes),
        cupLabel: getCup(Attributes),
        line: line,
        isLSProduct: LSModeEnabled,
      },
      {
        BasketLines: [
          {
            ProductId: Id,
            Quantity: qty,
            Price: getFinalPrice(Attributes),
          },
          {
            ProductId: Id,
            Quantity: qty,
            Price: getFinalPrice(Attributes),
          },
        ],
      }
    );
  }

  function getEditLine(): string | null {
    const match = pathname.match(EDIT_URL_REGEX);

    return match && match[0] && match[0].split('/')[2];
  }

  return {
    editLine: getEditLine(),
    addToBasket,
    updateBasket,
  };
}
